import { OnboardingState, SectionType } from './onboardingSlice'
import moment from 'moment'
import { SidebarItem } from 'features/Sidebar/SidebarShared'
import { Locations } from 'shared/Router/Locations'
import { isEmpty } from 'lodash-es'
import { getPlatformType } from 'shared/Router/Utils'
import Validators from 'shared/Validators'

/*
  If the isEmployeeOnboarding is true, then the employee is onboarding
  If stage is ASYNC_ITEMS employer is onboarding
*/
export const isOnboarding = (onboarding: OnboardingState | null) => {
  let platformType = getPlatformType()
  if (!onboarding || !onboarding.stage) {
    return false
  }
  // If the employee is onboarding, then the employee is onboarding
  if (platformType === 'employee' && onboarding.isEmployeeOnboarding) {
    return true
  }
  // If its on or before ASYNC_ITEMS, then the employer is onboarding
  if (
    platformType === 'employer' &&
    (onboarding.stage === 'WORKFORCE_SYNC' ||
      onboarding.stage === 'STRIPE_SYNC' ||
      onboarding.stage === 'ASYNC_ITEMS')
  ) {
    return true
  }
  return false
}

/*
  check if the employer is on sync getting started steps
  If they are on step WORKFORCE_SYNC, STRIPE_SYNC
*/
export const isEmployerGettingStartedOnboarding = (
  onboarding: OnboardingState | null
) => {
  if (!onboarding || !onboarding.stage) {
    return false
  }
  return (
    onboarding.stage === 'WORKFORCE_SYNC' || onboarding.stage === 'STRIPE_SYNC'
  )
}

const findSection = (onboarding: OnboardingState, sectionType: SectionType) => {
  try {
    return onboarding.sections.find(
      (section) => section.sectionType === sectionType
    )
  } catch (error) {
    return null
  }
}

const shouldSectionBeEnabled = (
  onboarding: OnboardingState,
  sectionType: SectionType
) => {
  const section = findSection(onboarding, sectionType)
  if (!section) {
    return false
  }
  return section.status !== 'BLOCKED'
}

/*
  When the employer is onboarding, only dashboard will be enabled
  or commuters page when the EMPLOYEE_INVITE section is enabled
  or program launch page when PROGRAM_LAUNCH section is enabled
*/
export const isEmployerSidebarItemDisabled = (
  onboarding: OnboardingState,
  link: SidebarItem
) => {
  // If its dashboard, then its always enabled
  if (link.link === Locations.Dashboard) {
    return false
  }
  // If its not onboarding everything is enabled
  if (!isOnboarding(onboarding)) {
    return false
  }
  // If the link is segment/overview and EMPLOYEE_INVITE section is enabled
  if (
    link.link === Locations.Segments.Overview &&
    shouldSectionBeEnabled(onboarding, 'EMPLOYEE_INVITE')
  ) {
    return false
  }
  if (
    (link.link === Locations.Programs.Launch ||
      link.link === Locations.Programs.Overview) &&
    shouldSectionBeEnabled(onboarding, 'PROGRAM_LAUNCH')
  ) {
    return false
  }
  return true
}

export const EmployeeSyncRow = (
  onboarding: OnboardingState,
  employerCreatedAt: string
) => {
  const section = findSection(onboarding, 'EMPLOYEE_SYNC')
  if (!section) {
    return null
  }

  const status = section.status

  const description = () => {
    if (status === 'IN_PROGRESS') {
      return 'We’re currently syncing your data. We’ll notify you of any issues.'
    }
    if (status === 'ATTENTION_NEEDED') {
      return 'We encountered an issue syncing your employee data. Please contact support or try again.'
    }
    return 'Sync your employees to your account'
  }

  const actionButton = () => {
    if (status === 'IN_PROGRESS') {
      return {
        text: 'Syncing...',
        onClick: () => {},
        disabled: true,
      }
    }
    if (status === 'ATTENTION_NEEDED') {
      return {
        text: 'Resync',
        onClick: () => {},
        disabled: false,
      }
    }
    return {
      text: 'Complete',
      onClick: () => {},
      disabled: true,
    }
  }
  return {
    title: 'Employee Sync ',
    owner: 'Fleet',
    description: description(),
    status: status,
    dueDate: moment(employerCreatedAt).add(7, 'day').format('MM/DD/YYYY'),
    actionButtonText: actionButton().text,
    onActionButtonClick: actionButton().onClick,
    actionButtonDisabled: actionButton().disabled,
  }
}

export const WorksiteSyncRow = (
  onboarding: OnboardingState,
  employerCreatedAt: string,
  onActionButtonClick: () => void
) => {
  const section = findSection(onboarding, 'WORKSITE_SYNC')
  if (!section) {
    return null
  }

  const status = section.status

  const description = () => {
    if (status === 'IN_PROGRESS') {
      return 'We’re currently syncing your data. We’ll notify you of any issues.'
    }
    if (status === 'ATTENTION_NEEDED') {
      return 'We encountered an issue syncing your worksite data. Please contact support or try again.'
    }
    return 'Sync your worksites to your account'
  }

  const actionButton = () => {
    if (status === 'BLOCKED') {
      return {
        text: 'Blocked',
        onClick: () => {},
        disabled: true,
      }
    }
    if (status === 'COMPLETED') {
      return {
        text: 'Complete',
        onClick: () => {},
        disabled: true,
      }
    }
    if (status === 'IN_PROGRESS') {
      return {
        text: 'Syncing...',
        onClick: () => {},
        disabled: true,
      }
    }
    if (status === 'ATTENTION_NEEDED') {
      return {
        text: 'Resync',
        onClick: onActionButtonClick,
        disabled: false,
      }
    }
    return {
      text: 'Complete',
      onClick: onActionButtonClick,
      disabled: false,
    }
  }
  return {
    title: 'Worksite Sync',
    owner: 'You',
    description: description(),
    status: status,
    dueDate: moment(employerCreatedAt).add(7, 'day').format('MM/DD/YYYY'),
    actionButtonText: actionButton().text,
    onActionButtonClick: actionButton().onClick,
    actionButtonDisabled: actionButton().disabled,
  }
}

export const FundingSourceVerificationRow = (
  onboarding: OnboardingState,
  employerCreatedAt: string
) => {
  const section = findSection(onboarding, 'FUNDING_SOURCE_VERIFICATION')
  if (!section) {
    return null
  }

  return {
    title: 'Add Funding Source',
    owner: 'You',
    description:
      'Funding source added successfully. We’ll notify you if additional information is needed.',
    status: section.status,
    dueDate: moment(employerCreatedAt).format('MM/DD/YYYY'),
    actionButtonText: 'Complete',
    onActionButtonClick: () => {},
    actionButtonDisabled: true,
  }
}

export const KYCCompletionRow = (
  onboarding: OnboardingState,
  employerCreatedAt: string,
  onActionButtonClick: () => void
) => {
  const section = findSection(onboarding, 'KYC_VERIFICATION')
  if (!section) {
    return null
  }
  const status = section.status

  const description = () => {
    if (status === 'IN_PROGRESS') {
      return 'KYC verification in progress.'
    }
    if (status === 'ATTENTION_NEEDED') {
      return 'We need additional information to complete your KYC verification. Please review and update your submission.'
    }
    return 'Complete your KYC verification'
  }

  const actionButton = () => {
    if (status === 'BLOCKED') {
      return {
        text: 'Blocked',
        onClick: () => {},
        disabled: true,
      }
    }
    if (status === 'COMPLETED') {
      return {
        text: 'Complete',
        onClick: () => {},
        disabled: true,
      }
    }
    if (status === 'IN_PROGRESS') {
      return {
        text: 'Syncing...',
        onClick: () => {},
        disabled: true,
      }
    }
    return {
      text: 'Complete',
      onClick: onActionButtonClick,
      disabled: false,
    }
  }

  return {
    title: 'KYC Completion Tracking',
    owner: 'You',
    description: description(),
    status: status,
    dueDate: moment(employerCreatedAt).add(10, 'day').format('MM/DD/YYYY'),
    actionButtonText: actionButton().text,
    onActionButtonClick: actionButton().onClick,
    actionButtonDisabled: actionButton().disabled,
  }
}

export const PayCadenceRow = (
  onboarding: OnboardingState,
  employerCreatedAt: string,
  onActionButtonClick: () => void
) => {
  const section = findSection(onboarding, 'PAYROLL_DATES')
  if (!section) {
    return null
  }
  return {
    title: 'Pay Cadence',
    owner: 'You',
    description: 'Set your pay cadence',
    status: section.status,
    dueDate: moment(employerCreatedAt).add(1, 'day').format('MM/DD/YYYY'),
    actionButtonText: 'Complete',
    onActionButtonClick: onActionButtonClick,
    actionButtonDisabled: true,
  }
}

export const CommuterBenefitSetupRow = (
  onboarding: OnboardingState,
  employerCreatedAt: string,
  onActionButtonClick: () => void
) => {
  const section = findSection(onboarding, 'COMMUTER_BENEFIT_SETUP')
  if (!section) {
    return null
  }
  const status = section.status
  const isComplete = status === 'COMPLETED'
  return {
    title: 'Commuter Benefit Setup',
    owner: 'You',
    description: isComplete
      ? 'Transit and parking payroll benefit details captured.'
      : 'Tell us if transit and parking benefits already exist in payroll.',
    status: status,
    dueDate: moment(employerCreatedAt).add(1, 'day').format('MM/DD/YYYY'),
    actionButtonText: isComplete ? 'Complete' : 'Complete',
    onActionButtonClick: isComplete ? () => {} : onActionButtonClick,
    actionButtonDisabled: isComplete,
  }
}

/*
  The due date will be the 1st of next month from the employer created at date
*/
export const ProgramLaunchRow = (
  onboarding: OnboardingState,
  employerCreatedAt: string,
  onActionButtonClick: () => void
) => {
  const section = findSection(onboarding, 'PROGRAM_LAUNCH')
  if (!section) {
    return null
  }
  const status = section.status
  const description = () => {
    if (status === 'BLOCKED') {
      return 'Complete prerequisite steps before launching program.'
    }
    return 'Launch your program'
  }
  const actionButton = () => {
    if (status === 'COMPLETED') {
      return {
        text: 'Complete',
        onClick: () => {},
        disabled: true,
      }
    }
    if (status === 'BLOCKED') {
      return {
        text: 'Blocked',
        onClick: () => {},
        disabled: true,
      }
    }
    return {
      text: 'Launch',
      onClick: onActionButtonClick,
      disabled: false,
    }
  }
  return {
    title: 'Launch Program',
    owner: 'You',
    description: description(),
    status: status,
    dueDate: moment(employerCreatedAt)
      .add(1, 'month')
      .startOf('month')
      .format('MM/DD/YYYY'),
    actionButtonText: actionButton().text,
    onActionButtonClick: actionButton().onClick,
    actionButtonDisabled: actionButton().disabled,
  }
}

export const EmployeeInviteRow = (
  onboarding: OnboardingState,
  employerCreatedAt: string,
  onActionButtonClick: () => void
) => {
  const section = findSection(onboarding, 'EMPLOYEE_INVITE')
  if (!section) {
    return null
  }
  const status = section.status
  const description = () => {
    if (status === 'BLOCKED') {
      return 'Complete prerequisite steps before inviting employees.'
    }
    return 'Invite your employees'
  }
  const actionButton = () => {
    if (status === 'COMPLETED') {
      return {
        text: 'Complete',
        onClick: () => {},
        disabled: true,
      }
    }
    if (status === 'BLOCKED') {
      return {
        text: 'Blocked',
        onClick: () => {},
        disabled: true,
      }
    }
    return {
      text: 'Invite',
      onClick: onActionButtonClick,
      disabled: false,
    }
  }
  return {
    title: 'Invite Commuters',
    owner: 'You',
    description: description(),
    status: status,
    dueDate: moment(employerCreatedAt)
      .add(1, 'month')
      .startOf('month')
      .format('MM/DD/YYYY'),
    actionButtonText: actionButton().text,
    onActionButtonClick: actionButton().onClick,
    actionButtonDisabled: actionButton().disabled,
  }
}

export const AdditionalDetailsRow = (
  onboarding: OnboardingState,
  onActionButtonClick: () => void
) => {
  const section = findSection(onboarding, 'ADDITIONAL_DETAILS')
  if (!section) {
    return null
  }
  const actionButton = () => {
    if (section.status === 'COMPLETED') {
      return {
        text: 'Complete',
        onClick: () => {},
        disabled: true,
      }
    }
    return {
      text: 'Complete',
      onClick: onActionButtonClick,
      disabled: false,
    }
  }
  return {
    title: 'Additional Details',
    owner: 'You',
    description: 'Add additional details to your profile',
    status: section.status,
    dueDate: moment().add(3, 'day').format('MM/DD/YYYY'),
    actionButtonText: actionButton().text,
    onActionButtonClick: actionButton().onClick,
    actionButtonDisabled: actionButton().disabled,
  }
}

export const hasAnyActivePrograms = (
  grhPrograms: object,
  qtfPrograms: object,
  micromobilityPrograms: object,
  flexPrograms: object
) => {
  return (
    !isEmpty(grhPrograms) ||
    !isEmpty(qtfPrograms) ||
    !isEmpty(micromobilityPrograms) ||
    !isEmpty(flexPrograms)
  )
}

// If any of the commuters have a hasInvite flag
// and the email is not same as the user email
// because we also invite the user when admin account is created
// then return true
export const hasAnyInvitedCommuters = (
  allCommuters: any[],
  userEmail: string
) => {
  return allCommuters.some(
    (commuter) => commuter.hasInvite && commuter.email !== userEmail
  )
}

// Error message constants for KYC forms
export const KYC_ERROR_MESSAGES = {
  REQUIRED_FIELD: (fieldName: string) => `Please enter ${fieldName}`,
  VALID_EMAIL: 'Please enter a valid email',
  SSN_LAST_4: 'Please enter the last 4 digits of your SSN',
  DATE_OF_BIRTH_REQUIRED: 'Please select your date of birth',
  AGE_18_PLUS: 'You must be at least 18 years old',
  FUTURE_DATE: 'Date of birth cannot be in the future',
  PHONE_REQUIRED: 'Please enter a phone number',
  VALID_PHONE: 'Please enter a valid phone number',
  EIN_REQUIRED: 'Please enter your Business EIN',
  VALID_EIN: 'Please enter a valid EIN in format XX-XXXXXXX',
  ZIP_REQUIRED: 'Please enter a zip code',
  VALID_ZIP: 'Please enter a valid zip code',
  OWNER_FIRST_NAME: "Please enter the owner's first name",
  OWNER_LAST_NAME: "Please enter the owner's last name",
  REPRESENTATIVE_FIRST_NAME: 'Please enter your first name',
  REPRESENTATIVE_LAST_NAME: 'Please enter your last name',
  TITLE_REQUIRED: 'Please enter a title',
  VALID_MONTH: 'Please enter a valid month',
  VALID_DAY: 'Please enter a valid day',
  VALID_YEAR: 'Please enter a valid year',
} as const

// Common validation functions for KYC forms
export const validateRequiredField = (
  value: string,
  fieldName: string,
  setErrorMessage: (message: string) => void
): boolean => {
  if (!value || value.trim() === '') {
    setErrorMessage(KYC_ERROR_MESSAGES.REQUIRED_FIELD(fieldName))
    return false
  }
  return true
}

export const validateEmail = (
  email: string,
  setErrorMessage: (message: string) => void
): boolean => {
  if (!Validators.checkEmail(email)) {
    setErrorMessage(KYC_ERROR_MESSAGES.VALID_EMAIL)
    return false
  }
  return true
}

export const validateSSNLast4 = (
  ssn: string,
  setErrorMessage: (message: string) => void
): boolean => {
  if (!ssn || ssn.length !== 4) {
    setErrorMessage(KYC_ERROR_MESSAGES.SSN_LAST_4)
    return false
  }
  return true
}

export const validateDateOfBirth = (
  date: Date | null,
  setErrorMessage: (message: string) => void
): boolean => {
  if (!date) {
    setErrorMessage(KYC_ERROR_MESSAGES.DATE_OF_BIRTH_REQUIRED)
    return false
  }

  const today = moment()
  const birthDate = moment(date)
  const age = today.diff(birthDate, 'years')

  if (age < 18) {
    setErrorMessage(KYC_ERROR_MESSAGES.AGE_18_PLUS)
    return false
  }

  if (birthDate.isAfter(today)) {
    setErrorMessage(KYC_ERROR_MESSAGES.FUTURE_DATE)
    return false
  }

  return true
}

export const validatePhoneNumber = (
  phone: string,
  setErrorMessage: (message: string) => void
): boolean => {
  if (!phone || phone.trim() === '') {
    setErrorMessage(KYC_ERROR_MESSAGES.PHONE_REQUIRED)
    return false
  }
  // Basic phone validation - can be enhanced as needed
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
  if (!phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''))) {
    setErrorMessage(KYC_ERROR_MESSAGES.VALID_PHONE)
    return false
  }
  return true
}

export const validateEIN = (
  ein: string,
  setErrorMessage: (message: string) => void
): boolean => {
  if (!ein || ein.trim() === '') {
    setErrorMessage(KYC_ERROR_MESSAGES.EIN_REQUIRED)
    return false
  }
  // Basic EIN validation - format XX-XXXXXXX
  const einRegex = /^\d{2}-\d{7}$/
  if (!einRegex.test(ein)) {
    setErrorMessage(KYC_ERROR_MESSAGES.VALID_EIN)
    return false
  }
  return true
}

export const validateZipCode = (
  zip: string,
  setErrorMessage: (message: string) => void
): boolean => {
  if (!zip || zip.trim() === '') {
    setErrorMessage(KYC_ERROR_MESSAGES.ZIP_REQUIRED)
    return false
  }
  // Basic zip code validation
  const zipRegex = /^\d{5}(-\d{4})?$/
  if (!zipRegex.test(zip)) {
    setErrorMessage(KYC_ERROR_MESSAGES.VALID_ZIP)
    return false
  }
  return true
}

export const validateDateComponents = (
  month: string,
  day: string,
  year: string,
  setErrorMessage: (message: string) => void
): boolean => {
  if (month === '' || parseInt(month) < 1 || parseInt(month) > 12) {
    setErrorMessage(KYC_ERROR_MESSAGES.VALID_MONTH)
    return false
  }
  if (day === '' || parseInt(day) < 1 || parseInt(day) > 31) {
    setErrorMessage(KYC_ERROR_MESSAGES.VALID_DAY)
    return false
  }
  if (
    year === '' ||
    parseInt(year) < 1900 ||
    parseInt(year) > moment().year()
  ) {
    setErrorMessage(KYC_ERROR_MESSAGES.VALID_YEAR)
    return false
  }

  // Create a Date object for validation
  const dateOfBirth = new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day)
  )
  return validateDateOfBirth(dateOfBirth, setErrorMessage)
}
